import { AxiosResponse } from "axios";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import * as actions from "./actions";
import * as actionTypes from "./actionTypes";
import * as types from "./types";
import * as services from "./service";
import * as alert from "../alert/actions";

function* fetchTargetListSaga(action: types.FetchTargetListReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDataTargetList | types.IApiError> = yield call(services.getDataTargetList, action.params);

        if (response.status === 200) {
            yield put(
                actions.fetchTargetListSuccess({
                    targets: response.data as types.IDataTargetList,
                })
            );
        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        yield put(
            actions.fetchTargetListFailure({
                error: e,
            })
        );
    }
}

function* fetchTargetDetailsSaga(action: types.FetchTargetReq) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDataTargetDetails | types.IApiError> = yield call(services.getTargetDetails, action.params);

        if (response.status === 200) {
            yield put(
                actions.fetchTargetDetailsSuccess({
                    target: response.data as types.IDataTargetDetails,
                })
            );
        }

        //Show IApiError
        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
        }

    } catch (e: any) {
        yield put(
            actions.fetchTargetDetailsFailure({
                error: e,
            })
        );
    }
}

function* postDataTargetSaga({ payload }: types.PostDataTargetRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPostDataTargetSuccess | types.IApiError> = yield call(services.postDataTarget, payload);

        if (response.status === 201) {
            let dataPayload = response.data as types.IPostDataTargetSuccess;
            yield put(
                actions.postDataTargetSuccess({
                    dataTarget: dataPayload,
                })
            );
            let successMsg = `The data target was successfully created: ${dataPayload.targetName}, with target ID: ${dataPayload.targetId}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 201) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail},, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));

        }
    } catch (e: any) {
        yield put(
            actions.postDataTargetFailure({
                error: e,
            })
        );
    }
}

function* deleteTargetSaga(action: types.DeleteDataTargetRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IDeleteDataTargetSuccess | types.IApiError> = yield call(services.deleteTarget, action.params);

        if (response.status === 204) {
            yield put(
                actions.deleteTargetSuccess({
                    success: true
                })
            );
            yield put(alert.enqueueSnackbar({ message: `The data target was successfully deleted, Timestamp: ${timestamp}`, options: { variant: "success" } }));

        }

        //Show IApiError
        if (response.status !== 204) {
            let apiError = response.data as types.IApiError;
            let errmsg = `StatusCode:${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errmsg, options: { variant: "error" } }));
            yield put(
                actions.deleteTargetFailure({
                    error: apiError
                })
            );
        }

    } catch (e: any) {
        yield put(
            actions.deleteTargetFailure({
                error: e,
            })
        );
    }
}



function* putDataTargetSaga({ targetId, payload }: types.PutDataTargetRequest) {
    const timestamp = new Date().toLocaleString();
    try {
        const response: AxiosResponse<types.IPutDataTargetSuccess | types.IApiError> = yield call(services.putDataTarget, targetId, payload);

        if (response.status === 200) {
            let dataPayload = response.data as types.IPutDataTargetSuccess;
            yield put(
                actions.putDataTargetSuccess({
                    dataTarget: dataPayload,
                })
            );
            let successMsg = `The data target was successfully edited. Target ID: ${targetId}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: successMsg, options: { variant: "success" } }));
        }

        if (response.status !== 200) {
            let apiError = response.data as types.IApiError;
            let errMsg = `StatusCode: ${apiError.errorCode} - ${apiError.errorType}. Details: ${apiError.detail}, Timestamp: ${timestamp}`;
            yield put(alert.enqueueSnackbar({ message: errMsg, options: { variant: "error" } }));
        }
    } catch (e: any) {
        yield put(
            actions.putDataTargetFailure({
                error: e,
            })
        );
    }
}


/*
  Watcher saga - intercept the action fired from view
  Starts worker saga on latest dispatched `FETCH_ACCESSCONTROL_REQUEST` action.
  Allows concurrent increments.
*/


function* targetListSaga() {
    yield takeLatest(actionTypes.FETCH_TARGET_LIST_REQUEST, fetchTargetListSaga);
}

function* targetDetailsSaga() {
    yield takeEvery(actionTypes.FETCH_TARGET_REQUEST, fetchTargetDetailsSaga);
}

function* createDataTargetSaga() {
    yield takeLatest(actionTypes.POST_DATATARGET_REQUEST, postDataTargetSaga);
}

function* deleteDataTargetSaga() {
    yield takeEvery(actionTypes.DELETE_TARGET_REQUEST, deleteTargetSaga);
}

function* editDataTargetSaga() {
    yield takeEvery(actionTypes.PUT_DATATARGET_REQUEST, putDataTargetSaga);
}


function* dataTargetSaga() {
    yield all([
        targetListSaga(),
        targetDetailsSaga(),
        createDataTargetSaga(),
        deleteDataTargetSaga(),
        editDataTargetSaga(),
    ])
};

export default dataTargetSaga;
